import React, { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  FaBuilding, FaCheckCircle, FaClock, FaBan, FaUsers, FaRupeeSign,
  FaSearch, FaFilter, FaEllipsisV, FaEye, FaEdit, FaCheck, FaTimes,
  FaPause, FaPlay, FaCreditCard, FaHistory, FaExternalLinkAlt, FaCopy,
  FaUserShield, FaDoorOpen, FaCalendarCheck, FaChartLine, FaArrowUp, FaArrowDown,
  FaIndustry, FaMapMarkerAlt, FaGlobe, FaKey, FaUserCheck, FaUserSlash, FaTrash,
} from 'react-icons/fa';
import { getApiUrl } from '../../config/api';
import { useSearchParams } from 'react-router-dom';
import './AdminCompanies.css';

const adminFetch = (endpoint, options = {}) => {
  const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  const token = localStorage.getItem('token');
  const headers = { ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (options.body && !headers['Content-Type'] && !headers['content-type']) {
    headers['Content-Type'] = 'application/json';
  }
  return fetch(getApiUrl(path), { ...options, headers }).then(async (res) => {
    const contentType = res.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      const preview = (await res.text()).slice(0, 120);
      throw new Error(
        preview.startsWith('<!DOCTYPE') || preview.startsWith('<html')
          ? 'Server returned HTML instead of JSON. Please ensure you are logged in as an admin and the backend is running.'
          : `Unexpected response (${res.status}): ${preview}`
      );
    }
    return res;
  });
};

const STATUS_OPTIONS = [
  { id: 'all', label: 'All Status' },
  { id: 'active', label: 'Active' },
  { id: 'pending', label: 'Pending' },
  { id: 'suspended', label: 'Suspended' },
  { id: 'rejected', label: 'Rejected' },
];

const PLAN_OPTIONS = [
  { id: 'all', label: 'All Plans' },
  { id: 'free_trial', label: 'Free Trial' },
  { id: 'starter', label: 'Starter' },
  { id: 'professional', label: 'Professional' },
  { id: 'enterprise', label: 'Enterprise' },
];

const PLAN_LIMITS = {
  free_trial: { employeeLimit: 10, meetingRoomLimit: 3, advancedFeatures: false },
  starter: { employeeLimit: 25, meetingRoomLimit: 10, advancedFeatures: false },
  professional: { employeeLimit: 100, meetingRoomLimit: 50, advancedFeatures: true },
  enterprise: { employeeLimit: 500, meetingRoomLimit: 200, advancedFeatures: true },
};

const formatDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

const formatDateTime = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);

const statusClass = (status) => {
  const map = { active: 'active', pending: 'pending', suspended: 'suspended', rejected: 'rejected' };
  return map[status] || 'pending';
};

const GrowthBadge = ({ value }) => {
  const num = Number(value) || 0;
  const positive = num >= 0;
  return (
    <span className={`acm-growth ${positive ? 'up' : 'down'}`}>
      {positive ? <FaArrowUp /> : <FaArrowDown />}
      {Math.abs(num).toFixed(1)}%
    </span>
  );
};

const StatCard = ({ icon: Icon, label, value, growth, gradient, loading }) => (
  <div className={`acm-stat-card ${gradient}`}>
    {loading ? (
      <div className="acm-skeleton acm-skeleton-stat" />
    ) : (
      <>
        <div className="acm-stat-icon"><Icon /></div>
        <div className="acm-stat-body">
          <span className="acm-stat-label">{label}</span>
          <span className="acm-stat-value">{value}</span>
          {growth != null && <GrowthBadge value={growth} />}
        </div>
      </>
    )}
  </div>
);

const ConfirmDialog = ({ open, title, message, confirmLabel, variant, onConfirm, onCancel }) => {
  if (!open) return null;
  return (
    <div className="acm-modal-overlay" onClick={onCancel} role="presentation">
      <div className="acm-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <h3>{title}</h3>
        <p>{message}</p>
        <div className="acm-modal-actions">
          <button type="button" className="acm-btn acm-btn-ghost" onClick={onCancel}>Cancel</button>
          <button type="button" className={`acm-btn acm-btn-${variant || 'primary'}`} onClick={onConfirm}>
            {confirmLabel || 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
};

const ActionsMenu = ({ company, onAction, onClose, anchorEl }) => {
  const ref = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (!anchorEl || !ref.current) return;

    const updatePosition = () => {
      const rect = anchorEl.getBoundingClientRect();
      const menuRect = ref.current.getBoundingClientRect();
      const menuWidth = menuRect.width || 210;
      const menuHeight = menuRect.height || 280;
      const gap = 6;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < menuHeight + gap;

      let top = openUp ? rect.top - menuHeight - gap : rect.bottom + gap;
      let left = rect.right - menuWidth;

      top = Math.max(8, Math.min(top, window.innerHeight - menuHeight - 8));
      left = Math.max(8, Math.min(left, window.innerWidth - menuWidth - 8));

      setPosition({ top, left });
    };

    updatePosition();
    requestAnimationFrame(updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [anchorEl]);

  useEffect(() => {
    const handler = (e) => {
      if (
        ref.current && !ref.current.contains(e.target) &&
        anchorEl && !anchorEl.contains(e.target)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose, anchorEl]);

  const items = [
    { id: 'view', label: 'View Details', icon: FaEye },
    { id: 'edit', label: 'Edit Company', icon: FaEdit },
    ...(company.status === 'pending'
      ? [
          { id: 'approve', label: 'Approve Company', icon: FaCheck },
          { id: 'reject', label: 'Reject Company', icon: FaTimes },
        ]
      : []),
    ...(company.status === 'active'
      ? [{ id: 'suspend', label: 'Suspend Company', icon: FaPause }]
      : []),
    ...(company.status === 'suspended'
      ? [{ id: 'reactivate', label: 'Reactivate Company', icon: FaPlay }]
      : []),
    { id: 'subscription', label: 'Manage Subscription', icon: FaCreditCard },
    { id: 'audit', label: 'View Audit Logs', icon: FaHistory },
    { id: 'delete', label: 'Delete Company', icon: FaTrash },
  ];

  return createPortal(
    <div
      className="acm-actions-menu acm-actions-menu-portal"
      ref={ref}
      style={{ top: position.top, left: position.left }}
    >
      {items.map(({ id, label, icon: Icon }) => (
        <button key={id} type="button" className={`acm-actions-item ${id}`} onClick={() => onAction(id)}>
          <Icon /> {label}
        </button>
      ))}
    </div>,
    document.body
  );
};

const AdminCompanies = () => {
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [industryFilter, setIndustryFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState('overview');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [subscriptionForm, setSubscriptionForm] = useState({});
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await adminFetch('/api/admin/companies/stats');
      const data = await res.json();
      if (data.success) setStats(data.stats);
    } catch {
      /* non-blocking */
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (planFilter !== 'all') params.set('plan', planFilter);
      if (industryFilter.trim()) params.set('industry', industryFilter.trim());

      const res = await adminFetch(`/api/admin/companies?${params.toString()}`);
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to load companies');
      setCompanies(data.companies || []);
    } catch (e) {
      setCompanies([]);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, planFilter, industryFilter]);

  const fetchDetail = useCallback(async (companyId) => {
    setDetailLoading(true);
    try {
      const res = await adminFetch(`/api/admin/companies/${companyId}`);
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to load details');
      setDetail(data);
      setSubscriptionForm({
        plan: data.subscription?.plan || 'free_trial',
        employeeLimit: data.subscription?.employeeLimit ?? 10,
        meetingRoomLimit: data.subscription?.meetingRoomLimit ?? 5,
        advancedFeatures: Boolean(data.subscription?.advancedFeatures),
        renewalDate: data.subscription?.renewalDate
          ? new Date(data.subscription.renewalDate).toISOString().slice(0, 10)
          : '',
      });
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  useEffect(() => {
    const q = searchParams.get('search');
    if (q) setSearch(q);
  }, [searchParams]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

  const openDrawer = async (company, tab = 'overview') => {
    setSelectedCompany(company);
    setDrawerTab(tab);
    setDrawerOpen(true);
    setEditMode(false);
    await fetchDetail(company._id);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedCompany(null);
    setDetail(null);
    setEditMode(false);
  };

  const refreshAll = async () => {
    await Promise.all([fetchStats(), fetchCompanies()]);
    if (selectedCompany) await fetchDetail(selectedCompany._id);
  };

  const updateStatus = async (companyId, status) => {
    try {
      const res = await adminFetch(`/api/admin/companies/${companyId}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message);
      showToast(`Company ${status}`);
      await refreshAll();
    } catch (e) {
      showToast(e.message, 'error');
    }
  };

  const deleteCompany = async (companyId) => {
    try {
      const res = await adminFetch(`/api/admin/companies/${companyId}`, {
        method: 'DELETE',
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message);
      showToast('Company deleted');
      if (selectedCompany?._id === companyId) closeDrawer();
      await refreshAll();
    } catch (e) {
      showToast(e.message, 'error');
    }
  };

  const handleAction = (company, actionId) => {
    setOpenMenuId(null);
    setMenuAnchor(null);
    switch (actionId) {
      case 'view':
        openDrawer(company, 'overview');
        break;
      case 'edit':
        openDrawer(company, 'overview');
        setEditMode(true);
        setEditForm({
          displayName: company.name,
          contactEmail: company.officialEmail,
          industry: company.industry !== '—' ? company.industry : '',
        });
        break;
      case 'approve':
        setConfirm({
          title: 'Approve Company',
          message: `Approve "${company.name}" and activate their workspace?`,
          confirmLabel: 'Approve',
          onConfirm: () => { setConfirm(null); updateStatus(company._id, 'active'); },
        });
        break;
      case 'reject':
        setConfirm({
          title: 'Reject Company',
          message: `Reject registration for "${company.name}"?`,
          confirmLabel: 'Reject',
          variant: 'danger',
          onConfirm: () => { setConfirm(null); updateStatus(company._id, 'rejected'); },
        });
        break;
      case 'suspend':
        setConfirm({
          title: 'Suspend Company',
          message: `Suspend "${company.name}"? Users will lose workspace access.`,
          confirmLabel: 'Suspend',
          variant: 'danger',
          onConfirm: () => { setConfirm(null); updateStatus(company._id, 'suspended'); },
        });
        break;
      case 'reactivate':
        setConfirm({
          title: 'Reactivate Company',
          message: `Restore access for "${company.name}"?`,
          confirmLabel: 'Reactivate',
          onConfirm: () => { setConfirm(null); updateStatus(company._id, 'active'); },
        });
        break;
      case 'subscription':
        openDrawer(company, 'subscription');
        break;
      case 'audit':
        openDrawer(company, 'audit');
        break;
      case 'delete':
        setConfirm({
          title: 'Delete Company',
          message: `Permanently delete "${company.name}"? This removes the company, its workspace members, meeting rooms, bookings and all related data. This cannot be undone.`,
          confirmLabel: 'Delete',
          variant: 'danger',
          onConfirm: () => { setConfirm(null); deleteCompany(company._id); },
        });
        break;
      default:
        break;
    }
  };

  const saveProfile = async () => {
    if (!selectedCompany) return;
    try {
      const res = await adminFetch(`/api/admin/companies/${selectedCompany._id}`, {
        method: 'PATCH',
        body: JSON.stringify(editForm),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message);
      showToast('Company profile updated');
      setEditMode(false);
      await refreshAll();
    } catch (e) {
      showToast(e.message, 'error');
    }
  };

  const saveSubscription = async () => {
    if (!selectedCompany) return;
    try {
      const res = await adminFetch(`/api/admin/companies/${selectedCompany._id}/subscription`, {
        method: 'PATCH',
        body: JSON.stringify(subscriptionForm),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message);
      showToast('Subscription updated');
      await refreshAll();
    } catch (e) {
      showToast(e.message, 'error');
    }
  };

  const handlePlanChange = (plan) => {
    const limits = PLAN_LIMITS[plan] || PLAN_LIMITS.free_trial;
    setSubscriptionForm((f) => ({
      ...f,
      plan,
      employeeLimit: limits.employeeLimit,
      meetingRoomLimit: limits.meetingRoomLimit,
      advancedFeatures: limits.advancedFeatures,
    }));
  };

  const updateEmployee = async (userId, payload) => {
    if (!selectedCompany) return;
    try {
      const res = await adminFetch(
        `/api/admin/companies/${selectedCompany._id}/employees/${userId}`,
        { method: 'PATCH', body: JSON.stringify(payload) }
      );
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message);
      showToast('Employee updated');
      await fetchDetail(selectedCompany._id);
    } catch (e) {
      showToast(e.message, 'error');
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard?.writeText(url);
    showToast('Workspace URL copied');
  };

  const filteredCount = companies.length;
  const growth = stats?.growthPercent ?? 0;

  const statCards = useMemo(() => [
    { icon: FaBuilding, label: 'Total Companies', value: stats?.totalCompanies ?? 0, growth, gradient: 'grad-blue' },
    { icon: FaCheckCircle, label: 'Active Companies', value: stats?.activeCompanies ?? 0, gradient: 'grad-green' },
    { icon: FaClock, label: 'Pending Approval', value: stats?.pendingCompanies ?? 0, gradient: 'grad-amber' },
    { icon: FaBan, label: 'Suspended', value: stats?.suspendedCompanies ?? 0, gradient: 'grad-red' },
    { icon: FaUsers, label: 'Total Employees', value: stats?.totalEmployees ?? 0, gradient: 'grad-purple' },
    { icon: FaRupeeSign, label: 'Monthly Revenue', value: formatCurrency(stats?.monthlyRevenue), gradient: 'grad-cyan' },
  ], [stats, growth]);

  return (
    <div className="admin-companies-page">
      <header className="acm-page-header">
        <div>
          <h1 className="acm-title">Company Management</h1>
          <p className="acm-subtitle">
            Monitor, approve, and manage all tenant workspaces from one centralized dashboard.
          </p>
        </div>
      </header>

      <div className="acm-stats-grid">
        {statCards.map((card) => (
          <StatCard key={card.label} {...card} loading={statsLoading} />
        ))}
      </div>

      <div className="acm-toolbar">
        <form
          className="acm-search-form"
          onSubmit={(e) => { e.preventDefault(); fetchCompanies(); }}
        >
          <FaSearch className="acm-search-icon" />
          <input
            type="search"
            placeholder="Search by company name, email, industry..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search companies"
          />
        </form>
        <button
          type="button"
          className={`acm-btn acm-btn-filter ${showFilters ? 'active' : ''}`}
          onClick={() => setShowFilters((v) => !v)}
        >
          <FaFilter /> Filters
        </button>
      </div>

      {showFilters && (
        <div className="acm-filters-panel">
          <div className="acm-filter-group">
            <label>Status</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              {STATUS_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </div>
          <div className="acm-filter-group">
            <label>Subscription Plan</label>
            <select value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}>
              {PLAN_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </div>
          <div className="acm-filter-group">
            <label>Industry Type</label>
            <input
              type="text"
              placeholder="e.g. Hospitality"
              value={industryFilter}
              onChange={(e) => setIndustryFilter(e.target.value)}
            />
          </div>
          <button type="button" className="acm-btn acm-btn-primary" onClick={fetchCompanies}>
            Apply Filters
          </button>
        </div>
      )}

      {error && <div className="acm-error-banner">{error}</div>}

      <div className="acm-table-wrap">
        <div className="acm-table-header">
          <span>{filteredCount} companies</span>
        </div>

        {loading ? (
          <div className="acm-table-skeleton">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="acm-skeleton acm-skeleton-row" />
            ))}
          </div>
        ) : companies.length === 0 ? (
          <div className="acm-empty-state">
            <FaBuilding />
            <h3>No companies found</h3>
            <p>Try adjusting your search or filters.</p>
          </div>
        ) : (
          <div className="acm-table-scroll">
            <table className="acm-table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Owner</th>
                  <th>Email</th>
                  <th>Workspace URL</th>
                  <th>Plan</th>
                  <th>Employees</th>
                  <th>Status</th>
                  <th>Registered</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {companies.map((c) => (
                  <tr key={c._id}>
                    <td>
                      <div className="acm-company-cell">
                        <div className="acm-company-logo">
                          {c.logoUrl ? (
                            <img src={c.logoUrl} alt="" />
                          ) : (
                            <span>{(c.name || 'C').charAt(0).toUpperCase()}</span>
                          )}
                        </div>
                        <div>
                          <button type="button" className="acm-link-btn" onClick={() => openDrawer(c)}>
                            {c.name}
                          </button>
                          <span className="acm-company-slug">{c.slug}</span>
                        </div>
                      </div>
                    </td>
                    <td>{c.ownerName}</td>
                    <td className="acm-email">{c.officialEmail}</td>
                    <td>
                      <div className="acm-url-cell">
                        <a href={c.workspaceUrl} target="_blank" rel="noopener noreferrer">
                          {c.slug}
                          <FaExternalLinkAlt />
                        </a>
                        <button type="button" className="acm-icon-btn" onClick={() => copyUrl(c.workspaceUrl)} title="Copy URL">
                          <FaCopy />
                        </button>
                      </div>
                    </td>
                    <td><span className="acm-plan-badge">{c.subscriptionPlanLabel}</span></td>
                    <td>{c.employeeCount}</td>
                    <td>
                      <span className={`acm-status-badge ${statusClass(c.status)}`}>{c.status}</span>
                    </td>
                    <td>{formatDate(c.registrationDate)}</td>
                    <td className="acm-actions-cell">
                      <div className="acm-actions-wrap">
                        <button
                          type="button"
                          className="acm-icon-btn acm-menu-trigger"
                          onClick={(e) => {
                            if (openMenuId === c._id) {
                              setOpenMenuId(null);
                              setMenuAnchor(null);
                            } else {
                              setOpenMenuId(c._id);
                              setMenuAnchor(e.currentTarget);
                            }
                          }}
                          aria-label="Actions"
                          aria-expanded={openMenuId === c._id}
                        >
                          <FaEllipsisV />
                        </button>
                        {openMenuId === c._id && (
                          <ActionsMenu
                            company={c}
                            anchorEl={menuAnchor}
                            onAction={(id) => handleAction(c, id)}
                            onClose={() => { setOpenMenuId(null); setMenuAnchor(null); }}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Drawer */}
      <div className={`acm-drawer-overlay ${drawerOpen ? 'open' : ''}`} onClick={closeDrawer} role="presentation" />
      <aside className={`acm-drawer ${drawerOpen ? 'open' : ''}`} aria-hidden={!drawerOpen}>
        {selectedCompany && (
          <>
            <div className="acm-drawer-banner">
              {detail?.company?.bannerUrl ? (
                <img src={detail.company.bannerUrl} alt="" className="acm-banner-img" />
              ) : (
                <div className="acm-banner-placeholder" />
              )}
              <button type="button" className="acm-drawer-close" onClick={closeDrawer} aria-label="Close">
                <FaTimes />
              </button>
              <div className="acm-drawer-brand">
                <div className="acm-drawer-logo">
                  {detail?.company?.logoUrl ? (
                    <img src={detail.company.logoUrl} alt="" />
                  ) : (
                    <span>{(selectedCompany.name || 'C').charAt(0)}</span>
                  )}
                </div>
                <div>
                  <h2>{detail?.company?.name || selectedCompany.name}</h2>
                  <span className={`acm-status-badge ${statusClass(detail?.company?.status || selectedCompany.status)}`}>
                    {detail?.company?.status || selectedCompany.status}
                  </span>
                </div>
              </div>
            </div>

            <nav className="acm-drawer-tabs">
              {['overview', 'employees', 'subscription', 'audit'].map((tab) => (
                <button
                  key={tab}
                  type="button"
                  className={drawerTab === tab ? 'active' : ''}
                  onClick={() => setDrawerTab(tab)}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </nav>

            <div className="acm-drawer-body">
              {detailLoading ? (
                <div className="acm-drawer-loading">
                  <div className="admin-spinner" />
                  <p>Loading company details...</p>
                </div>
              ) : (
                <>
                  {drawerTab === 'overview' && detail && (
                    <div className="acm-tab-panel">
                      {editMode ? (
                        <div className="acm-edit-form">
                          <h3>Edit Company</h3>
                          <label>Display Name<input value={editForm.displayName || ''} onChange={(e) => setEditForm({ ...editForm, displayName: e.target.value })} /></label>
                          <label>Contact Email<input value={editForm.contactEmail || ''} onChange={(e) => setEditForm({ ...editForm, contactEmail: e.target.value })} /></label>
                          <label>Industry<input value={editForm.industry || ''} onChange={(e) => setEditForm({ ...editForm, industry: e.target.value })} /></label>
                          <div className="acm-form-actions">
                            <button type="button" className="acm-btn acm-btn-ghost" onClick={() => setEditMode(false)}>Cancel</button>
                            <button type="button" className="acm-btn acm-btn-primary" onClick={saveProfile}>Save Changes</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <section className="acm-info-section">
                            <h3>Company Information</h3>
                            <div className="acm-info-grid">
                              <div><FaIndustry /><span>Industry</span><strong>{detail.company.industry || '—'}</strong></div>
                              <div><FaKey /><span>GST Number</span><strong>{detail.company.gstNumber || '—'}</strong></div>
                              <div><FaGlobe /><span>Website</span><strong>{detail.company.website || '—'}</strong></div>
                              <div><FaMapMarkerAlt /><span>Address</span><strong>
                                {[detail.company.address?.line1, detail.company.address?.city, detail.company.address?.state]
                                  .filter(Boolean).join(', ') || '—'}
                              </strong></div>
                              <div><FaCalendarCheck /><span>Registered</span><strong>{formatDate(detail.company.registrationDate)}</strong></div>
                              <div><FaExternalLinkAlt /><span>Workspace</span>
                                <a href={detail.company.workspaceUrl} target="_blank" rel="noopener noreferrer">
                                  Open workspace
                                </a>
                              </div>
                            </div>
                          </section>

                          <section className="acm-usage-section">
                            <h3>Usage Overview</h3>
                            <div className="acm-usage-grid">
                              {[
                                { icon: FaUsers, label: 'Employees', value: detail.usage.totalEmployees },
                                { icon: FaUserShield, label: 'Managers', value: detail.usage.managers },
                                { icon: FaUsers, label: 'Staff', value: detail.usage.staffMembers },
                                { icon: FaDoorOpen, label: 'Meeting Rooms', value: detail.usage.meetingRooms },
                                { icon: FaCalendarCheck, label: 'Bookings', value: detail.usage.totalBookings },
                                { icon: FaClock, label: 'Pending Requests', value: detail.usage.pendingRequests },
                              ].map(({ icon: Icon, label, value }) => (
                                <div key={label} className="acm-usage-card">
                                  <Icon />
                                  <span>{label}</span>
                                  <strong>{value}</strong>
                                </div>
                              ))}
                            </div>
                          </section>
                        </>
                      )}
                    </div>
                  )}

                  {drawerTab === 'employees' && detail && (
                    <div className="acm-tab-panel">
                      <h3>Employee Management</h3>
                      {detail.employees.length === 0 ? (
                        <div className="acm-empty-state small">
                          <FaUsers />
                          <p>No employees registered yet.</p>
                        </div>
                      ) : (
                        <div className="acm-emp-table-wrap">
                          <table className="acm-emp-table">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Role</th>
                                <th>Department</th>
                                <th>Status</th>
                                <th>Last Login</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {detail.employees.map((emp) => (
                                <tr key={emp._id}>
                                  <td>{emp.name}</td>
                                  <td>{emp.email}</td>
                                  <td>
                                    <select
                                      value={emp.role}
                                      onChange={(e) => updateEmployee(emp._id, { role: e.target.value })}
                                      className="acm-inline-select"
                                    >
                                      <option value="company_admin">Admin</option>
                                      <option value="company_manager">Manager</option>
                                      <option value="company_staff">Staff</option>
                                    </select>
                                  </td>
                                  <td>{emp.department}</td>
                                  <td>
                                    <span className={`acm-status-badge ${emp.status === 'active' ? 'active' : 'suspended'}`}>
                                      {emp.status}
                                    </span>
                                  </td>
                                  <td>{formatDateTime(emp.lastLoginAt)}</td>
                                  <td>
                                    <div className="acm-emp-actions">
                                      {emp.status === 'blocked' ? (
                                        <button type="button" title="Activate" onClick={() => updateEmployee(emp._id, { status: 'active' })}>
                                          <FaUserCheck />
                                        </button>
                                      ) : (
                                        <button type="button" title="Block" onClick={() => updateEmployee(emp._id, { status: 'blocked' })}>
                                          <FaUserSlash />
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {drawerTab === 'subscription' && detail && (
                    <div className="acm-tab-panel">
                      <h3>Subscription Management</h3>
                      <div className="acm-sub-grid">
                        {PLAN_OPTIONS.filter((p) => p.id !== 'all').map((plan) => (
                          <button
                            key={plan.id}
                            type="button"
                            className={`acm-plan-card ${subscriptionForm.plan === plan.id ? 'selected' : ''}`}
                            onClick={() => handlePlanChange(plan.id)}
                          >
                            <FaChartLine />
                            <span>{plan.label}</span>
                          </button>
                        ))}
                      </div>
                      <div className="acm-sub-details">
                        <label>
                          Employee Limit
                          <input
                            type="number"
                            value={subscriptionForm.employeeLimit}
                            onChange={(e) => setSubscriptionForm({ ...subscriptionForm, employeeLimit: e.target.value })}
                          />
                        </label>
                        <label>
                          Meeting Room Limit
                          <input
                            type="number"
                            value={subscriptionForm.meetingRoomLimit}
                            onChange={(e) => setSubscriptionForm({ ...subscriptionForm, meetingRoomLimit: e.target.value })}
                          />
                        </label>
                        <label>
                          Renewal Date
                          <input
                            type="date"
                            value={subscriptionForm.renewalDate}
                            onChange={(e) => setSubscriptionForm({ ...subscriptionForm, renewalDate: e.target.value })}
                          />
                        </label>
                        <label className="acm-checkbox-label">
                          <input
                            type="checkbox"
                            checked={subscriptionForm.advancedFeatures}
                            onChange={(e) => setSubscriptionForm({ ...subscriptionForm, advancedFeatures: e.target.checked })}
                          />
                          Advanced Features Enabled
                        </label>
                        <div className="acm-storage-bar">
                          <span>Storage: {detail.subscription.storageUsedGb} / {detail.subscription.storageLimitGb} GB</span>
                          <div className="acm-storage-track">
                            <div
                              className="acm-storage-fill"
                              style={{
                                width: `${Math.min(100, (detail.subscription.storageUsedGb / detail.subscription.storageLimitGb) * 100)}%`,
                              }}
                            />
                          </div>
                        </div>
                      </div>
                      <button type="button" className="acm-btn acm-btn-primary" onClick={saveSubscription}>
                        Save Subscription
                      </button>
                    </div>
                  )}

                  {drawerTab === 'audit' && detail && (
                    <div className="acm-tab-panel">
                      <h3>Audit Logs</h3>
                      {detail.auditLogs.length === 0 ? (
                        <div className="acm-empty-state small">
                          <FaHistory />
                          <p>No activity recorded yet.</p>
                        </div>
                      ) : (
                        <div className="acm-timeline">
                          {detail.auditLogs.map((log) => (
                            <div key={log._id} className="acm-timeline-item">
                              <div className="acm-timeline-dot" />
                              <div className="acm-timeline-content">
                                <strong>{log.summary || log.action}</strong>
                                <span>{log.actorName} · {formatDateTime(log.createdAt)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </aside>

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        variant={confirm?.variant}
        onConfirm={confirm?.onConfirm}
        onCancel={() => setConfirm(null)}
      />

      {toast && (
        <div className={`acm-toast ${toast.type}`} role="status">
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default AdminCompanies;
